import React, { useState, useEffect } from 'react'
import { Col, Row, Spinner } from 'react-bootstrap'
import DataTable, { createTheme } from 'react-data-table-component'
import BarHeader from './common/BarHeader'
import { FaUsers, FaDatabase, FaFileCsv, FaFileExcel } from 'react-icons/fa'
import { GoGraph } from 'react-icons/go'
import { VscChevronDown, VscChevronRight } from 'react-icons/vsc'
import { getAdminUsers, getAdminToricResults, getAdminConstants, getAdminIolExchangeResults } from './services/AdminServices'
import { formatDiopters, formatAxis } from './iol_exchange/IOLExchangeCalculation'
import * as XLSX from 'xlsx'
import './adminPage.css'

createTheme('medicalDark', {
    text: {
        primary: '#ffffff',
        secondary: '#cccccc',
    },
    background: {
        default: 'transparent',
    },
    context: {
        background: '#00a8ff',
        text: '#ffffff',
    },
    divider: {
        default: 'rgba(255, 255, 255, 0.1)',
    },
    button: {
        default: '#00a8ff',
        hover: 'rgba(0, 168, 255, 0.2)',
        focus: 'rgba(0, 168, 255, 0.4)',
        focusLight: 'rgba(0, 168, 255, 0.4)',
    },
    sortFocus: {
        default: '#00a8ff',
    },
})

export default function AdminPage() {
    const [activeView, setActiveView] = useState('Constant Optimizator')
    const [isIolMenuOpen, setIsIolMenuOpen] = useState(true)
    const [isToricMenuOpen, setIsToricMenuOpen] = useState(false)

    const [isLoading, setIsLoading] = useState(false)
    const [tableData, setTableData] = useState([])
    // IOL Exchange: real patients by default; the test runs left thousands of "Test" cases.
    const [iolxCaseType, setIolxCaseType] = useState('Real')
    const [loadError, setLoadError] = useState(null)

    useEffect(() => {
        fetchDataForView(activeView)
    }, [activeView, iolxCaseType])

    async function fetchDataForView(viewName) {
        setIsLoading(true)
        setTableData([])
        setLoadError(null)

        try {
            if (viewName === 'IOL Exchange') {
                setTableData(await getAdminIolExchangeResults(iolxCaseType))
            } else if (viewName === 'Users') {
                const users = await getAdminUsers()
                setTableData(users)
            } else if (viewName === 'Constant Optimizator') {
                const constants = await getAdminConstants()
                setTableData(constants)
            } else {
                const allToricResults = await getAdminToricResults()
                
                let filteredResults = []
                if (viewName === 'Standard Toric IOL') {
                    filteredResults = allToricResults.filter(r => 
                        r.iolType == "Standard"
                    )
                } else if (viewName === 'Phakic / Piggyback Aligner') {
                    filteredResults = allToricResults.filter(r => 
                        r.iolType === 'Phakic/Piggyback' || 
                        r.iolType === 'Piggyback Toric' ||
                        r.iolType === 'STAAR ICL' ||
                        r.iolType === 'Eyecryl Phakic IOL'
                    )
                } else if (viewName === 'SIA Calculator') {
                    filteredResults = allToricResults.filter(r =>
                        r.iolToricity === -1
                    )
                }

                filteredResults = filteredResults.filter(r => r.userId !== 7)
                setTableData(filteredResults)
            }
        } catch (error) {
            console.error(error)
            if (viewName === 'IOL Exchange') {
                setLoadError((error.response && error.response.status === 403)
                    ? 'Only an admin account can see these results. Sign in again if you just became admin.'
                    : 'The results could not be loaded.')
            }
        } finally {
            setIsLoading(false)
        }
    }

    const columnsUsers = [
        { name: 'ID', selector: row => row.id, sortable: true, width: '80px' },
        { name: 'Email', selector: row => row.email, sortable: true },
        { name: 'Country', selector: row => row.country, sortable: true },
        { name: 'Role', selector: row => row.role, sortable: true },
        { name: 'Enabled', selector: row => row.enabled ? 'Yes' : 'No', sortable: true },
    ]

    const columnsConstants = [
        { name: 'ID', selector: row => row.id, sortable: true, width: '70px' },
        { name: 'UserID', selector: row => row.userId, sortable: true, width: '90px' },
        { name: 'Date', selector: row => {
            if (!row.timeStamp) return '-'
            const date = new Date(row.timeStamp)
            return date.toLocaleDateString()
        }, sortable: true, width: '110px' },
        { name: 'Patients', selector: row => row.patients, sortable: true, width: '100px' },
        { name: 'Formula', selector: row => row.formulaName || '-', sortable: true, width: '120px' },
        { name: 'IOL Manufacturer', selector: row => row.iolmanufacturer || '-', sortable: true },
        { name: 'IOL Model', selector: row => row.iolmodel || '-', sortable: true },
        { name: 'Biometer', selector: row => row.biometer || '-', sortable: true, width: '140px' },
        { name: 'IOL Power', selector: row => row.power, sortable: true, width: '100px' },
        { name: 'Mean PE', selector: row => row.meanPE, sortable: true, width: '100px' },
        { name: 'Constant', selector: row => row.constant, sortable: true, width: '100px' },
        { name: 'Result', selector: row => parseFloat(row.result).toFixed(2), sortable: true, width: '100px' },
        { name: 'Observations', selector: row => row.observations || '-', sortable: false }
    ]

    function getAverageMagAxis(arr) {
        if (!arr || arr.length < 6) return '-'
        let mags = [parseFloat(arr[0]), parseFloat(arr[2]), parseFloat(arr[4])].filter(n => !Number.isNaN(n))
        let axes = [parseFloat(arr[1]), parseFloat(arr[3]), parseFloat(arr[5])].filter(n => !Number.isNaN(n))
        
        let avgMag = mags.length ? (mags.reduce((a, b) => a + b, 0) / mags.length).toFixed(2) : '-'
        let avgAxis = axes.length ? Math.round(axes.reduce((a, b) => a + b, 0) / axes.length) : '-'
        
        if (avgMag === '-' && avgAxis === '-') return '-'
        return `${avgMag} @ ${avgAxis}°`
    }

    const columnsStandardToric = [
        { name: 'ID', selector: row => row.id, sortable: true, width: '70px' },
        { name: 'Date', selector: row => row.date, sortable: true, width: '105px' },
        { name: 'User', selector: row => row.userId != 0 ? row.userId : '-', sortable: true, width: '80px' },
        { name: 'Patient', selector: row => row.patientID || row.patientId || '-', sortable: true },
        { name: 'Model', selector: row => row.iolModel || '-', sortable: true },
        { name: 'Toric', selector: row => row.iolToricity !== undefined && row.iolToricity !== -1 ? row.iolToricity : '-', sortable: true, width: '70px' },
        { name: 'Biometer 1', selector: row => getAverageMagAxis(row.tca1 || row.TCA1), sortable: false, width: '110px' },
        { name: 'Biometer 2', selector: row => getAverageMagAxis(row.tca2 || row.TCA2), sortable: false, width: '110px' },
        { name: 'Measured 1', selector: row => getAverageMagAxis(row.tca3 || row.TCA3), sortable: false, width: '110px' },
        { name: 'Measured 2', selector: row => getAverageMagAxis(row.tca4 || row.TCA4), sortable: false, width: '110px' },
        { name: 'Final TCA', selector: row => {
            const t = row.TCA || row.tca
            if (!t || t[0] == null || t[1] == null) return '-'
            const mag = parseFloat(t[0])
            const axis = parseFloat(t[1])
            if (Number.isNaN(mag) || Number.isNaN(axis) || mag === 0 || axis === 0) return '-'
            return `${mag.toFixed(2)} @ ${axis}°`
        }, sortable: true, width: '110px' },
        { name: 'Final SIA', selector: row => {
            const s = row.SIA || row.sia
            if (!s || s[0] == null || s[1] == null) return '-'
            const mag = parseFloat(s[0])
            const axis = parseFloat(s[1])
            if (Number.isNaN(mag) || Number.isNaN(axis) || mag === 0 || axis === 0) return '-'
            return `${mag.toFixed(2)} @ ${axis}°`
        }, sortable: true, width: '110px' },
        { name: 'SICA1', selector: row => {
            const sc = row.SICA1 || row.sica1
            return sc && sc[0] && sc[1] ? `${parseFloat(sc[0]).toFixed(2)} @ ${sc[1]}°` : '-'
        }, sortable: true, width: '110px' }
    ]

    const columnsPhakic = [
        { name: 'ID', selector: row => row.id, sortable: true, width: '70px' },
        { name: 'Date', selector: row => row.date, sortable: true, width: '105px' },
        { name: 'User', selector: row => row.userId != 0 ? row.userId : '-', sortable: true, width: '80px' },
        { name: 'Patient', selector: row => row.patientID || row.patientId || '-', sortable: true },
        { name: 'IOL Type', selector: row => row.iolType || '-', sortable: true, width: '140px' },
        { name: 'Model', selector: row => row.iolModel || '-', sortable: true },
        { name: 'Toric', selector: row => row.iolToricity !== undefined && row.iolToricity !== -1 ? row.iolToricity : '-', sortable: true, width: '70px' },
        { name: 'Biometer 1', selector: row => getAverageMagAxis(row.tca1 || row.TCA1), sortable: false, width: '110px' },
        { name: 'Biometer 2', selector: row => getAverageMagAxis(row.tca2 || row.TCA2), sortable: false, width: '110px' },
        { name: 'Measured 1', selector: row => getAverageMagAxis(row.tca3 || row.TCA3), sortable: false, width: '110px' },
        { name: 'Measured 2', selector: row => getAverageMagAxis(row.tca4 || row.TCA4), sortable: false, width: '110px' },
        { name: 'Final TCA', selector: row => {
            const t = row.TCA || row.tca
            if (!t || t[0] == null || t[1] == null) return '-'
            const mag = parseFloat(t[0])
            const axis = parseFloat(t[1])
            if (Number.isNaN(mag) || Number.isNaN(axis) || mag === 0 || axis === 0) return '-'
            return `${mag.toFixed(2)} @ ${axis}°`
        }, sortable: true, width: '110px' },
        { name: 'Final SIA', selector: row => {
            const s = row.SIA || row.sia
            if (!s || s[0] == null || s[1] == null) return '-'
            const mag = parseFloat(s[0])
            const axis = parseFloat(s[1])
            if (Number.isNaN(mag) || Number.isNaN(axis) || mag === 0 || axis === 0) return '-'
            return `${mag.toFixed(2)} @ ${axis}°`
        }, sortable: true, width: '110px' },
        { name: 'SICA1', selector: row => {
            const sc = row.SICA1 || row.sica1
            if (!sc || sc[0] == null || sc[1] == null) return '-'
            const mag = parseFloat(sc[0])
            const axis = parseFloat(sc[1])
            if (Number.isNaN(mag) || Number.isNaN(axis) || mag === 0 || axis === 0) return '-'
            return `${mag.toFixed(2)} @ ${axis}°`
        }, sortable: true, width: '110px' },
        { name: 'Visible', selector: row => row.visible ? 'Yes' : 'No', sortable: true, width: '80px' }
    ]

    const columnsSIACalculator = [
        { name: 'ID', selector: row => row.id, sortable: true, width: '70px' },
        { name: 'Date', selector: row => row.date, sortable: true, width: '105px' },
        { name: 'User', selector: row => row.userId != 0 ? row.userId : '-', sortable: true, width: '80px' },
        { name: 'Patient', selector: row => row.patientID || row.patientid || '-', sortable: true },
        { name: 'Gender', selector: row => row.gender || '-', sortable: true },
        { name: 'IOL', selector: row => row.iolType || '-', sortable: true },
        { name: 'Inc. Size', selector: row => row.postopInput && row.postopInput[0] ? row.postopInput[0] : '-', sortable: true, width: '110px' },
        { name: 'Inc. Location', selector: row => row.postopInput && row.postopInput[1] ? row.postopInput[1] : '-', sortable: true },
        { name: 'Preop. TCA', selector: row => getAverageMagAxis(row.tca1 || row.TCA1), sortable: false, width: '110px' },
        { name: 'Postop. TCA', selector: row => getAverageMagAxis(row.tca2 || row.TCA2), sortable: false, width: '110px' },
    ]

    // IOL Exchange (iol_exchange_results, one row per calculation). Dioptres as the
    // calculator prints them: 2 decimals with sign.
    const dpt = v => (v === null || v === undefined) ? '-' : formatDiopters(Number(v))
    const pow = v => (v === null || v === undefined) ? '-' : Number(v).toFixed(1)
    const iolOf = (model, aConstant) => model || (aConstant != null ? 'A ' + Number(aConstant).toFixed(2) : '-')
    const columnsIolExchange = [
        { name: 'ID', selector: row => row.id, sortable: true, width: '70px' },
        { name: 'Date', selector: row => row.createdAt ? row.createdAt.slice(0, 16).replace('T', ' ') : '-', sortable: true, width: '140px' },
        { name: 'User', selector: row => row.userId != null ? row.userId : '-', sortable: true, width: '75px' },
        { name: 'Case', selector: row => row.caseType || '-', sortable: true, width: '75px' },
        { name: 'Current IOL', selector: row => iolOf(row.currentIolModel, row.currentAConstant), sortable: true, wrap: true },
        { name: 'Power', selector: row => pow(row.currentIolPower), sortable: true, width: '80px' },
        { name: 'Refraction', selector: row => dpt(row.sphere) + ' ' + dpt(row.cylinder) + (row.axis ? ' x ' + row.axis : ''), sortable: false, width: '150px' },
        { name: 'MRSE', selector: row => dpt(row.currentMrse), sortable: true, width: '80px' },
        { name: 'Target', selector: row => dpt(row.targetMrse), sortable: true, width: '80px' },
        { name: 'New IOL', selector: row => row.changeIolModel ? iolOf(row.newIolModel, row.newAConstant) : 'Same model', sortable: true, wrap: true },
        { name: 'Position', selector: row => row.newPosition || '-', sortable: true, width: '90px' },
        { name: 'Rec. power', selector: row => pow(row.recommendedPower), sortable: true, width: '100px' },
        { name: 'Pred. MRSE', selector: row => dpt(row.recommendedMrse), sortable: true, width: '100px' },
        { name: 'Toric', selector: row => row.toricSelectedCylinder != null
            ? Number(row.toricSelectedCylinder).toFixed(2) + ' D @ ' + formatAxis(row.toricSuggestedAxis)
            : (row.useToric === 'Yes' ? 'No result' : '-'), sortable: false, width: '130px' },
        { name: 'Reasons', selector: row => (row.reasons || []).map(r => r === 'Other' && row.otherReason ? 'Other: ' + row.otherReason : r).join(', ') || '-', sortable: false, wrap: true },
        { name: 'Warnings', selector: row => (row.warnings || []).join(', ') || '-', sortable: false, wrap: true },
    ]

    function getActiveColumns() {
        if (activeView === 'IOL Exchange') return columnsIolExchange
        if (activeView === 'Users') return columnsUsers
        if (activeView === 'Constant Optimizator') return columnsConstants
        if (activeView === 'Standard Toric IOL') return columnsStandardToric
        if (activeView === 'Phakic / Piggyback Aligner') return columnsPhakic
        if (activeView === 'SIA Calculator') return columnsSIACalculator
    }

    function exportData(format) {
        if (tableData.length === 0) return
        
        const columns = getActiveColumns()
        const dataToExport = tableData.map(row => {
            const exportRow = {}
            columns.forEach(col => {
                if (col.name && col.selector) {
                    exportRow[col.name] = col.selector(row)
                }
            })
            return exportRow
        })

        const worksheet = XLSX.utils.json_to_sheet(dataToExport)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Results')
        
        const fileName = `${activeView.replace(/ /g, '_')}_${new Date().toISOString().split('T')[0]}`
        
        if (format === 'csv') {
            XLSX.writeFile(workbook, `${fileName}.csv`)
        } else {
            XLSX.writeFile(workbook, `${fileName}.xlsx`)
        }
    }

    function renderAverages() {
        if (tableData.length === 0) return null

        if (activeView === 'IOL Exchange') {
            const count = test => tableData.filter(test).length
            const reasons = {}
            tableData.forEach(r => (r.reasons || []).forEach(x => { reasons[x] = (reasons[x] || 0) + 1 }))
            const topReason = Object.keys(reasons).sort((a, b) => reasons[b] - reasons[a])[0]
            return (
                <div className="averagesContainer">
                    <span><strong>Cases:</strong> {tableData.length}</span>
                    <span><strong>Model change:</strong> {count(r => r.changeIolModel)}</span>
                    <span><strong>Sulcus:</strong> {count(r => r.newPosition === 'Sulcus')}</span>
                    <span><strong>Toric:</strong> {count(r => r.useToric === 'Yes')}</span>
                    <span><strong>No recommendation:</strong> {count(r => r.recommendedPower == null)}</span>
                    <span><strong>Top reason:</strong> {topReason ? topReason + ' (' + reasons[topReason] + ')' : '-'}</span>
                </div>
            )
        }

        if (activeView === 'Standard Toric IOL' || activeView === 'Phakic / Piggyback Aligner') {
            let tcaSum = 0, tcaCount = 0
            let siaSum = 0, siaCount = 0
            let sicaSum = 0, sicaCount = 0

            tableData.forEach(row => {
                const tca = row.TCA || row.tca
                if (tca && tca[0] != null && !Number.isNaN(parseFloat(tca[0])) && parseFloat(tca[0]) !== 0) {
                    tcaSum += parseFloat(tca[0])
                    tcaCount++
                }
                const sia = row.SIA || row.sia
                if (sia && sia[0] != null && !Number.isNaN(parseFloat(sia[0])) && parseFloat(sia[0]) !== 0) {
                    siaSum += parseFloat(sia[0])
                    siaCount++
                }
                const sica = row.SICA1 || row.sica1
                if (sica && sica[0] != null && !Number.isNaN(parseFloat(sica[0])) && parseFloat(sica[0]) !== 0) {
                    sicaSum += parseFloat(sica[0])
                    sicaCount++
                }
            })

            const avgTCA = tcaCount > 0 ? (tcaSum / tcaCount).toFixed(2) : '-'
            const avgSIA = siaCount > 0 ? (siaSum / siaCount).toFixed(2) : '-'
            const avgSICA = sicaCount > 0 ? (sicaSum / sicaCount).toFixed(2) : '-'

            return (
                <div className="averagesContainer">
                    <span><strong>Avg TCA Mag:</strong> {avgTCA}°</span>
                    <span><strong>Avg SIA Mag:</strong> {avgSIA}°</span>
                    <span><strong>Avg SICA1 Mag:</strong> {avgSICA}°</span>
                </div>
            )
        }

        if (activeView === 'SIA Calculator') {
            let incSum = 0, incCount = 0
            tableData.forEach(row => {
                const inc = row.postopInput && row.postopInput[0]
                if (inc != null && !Number.isNaN(parseFloat(inc))) {
                    incSum += parseFloat(inc)
                    incCount++
                }
            })
            
            const avgInc = incCount > 0 ? (incSum / incCount).toFixed(2) : '-'
            return (
                <div className="averagesContainer">
                    <span><strong>Avg Incision Size:</strong> {avgInc} mm</span>
                </div>
            )
        }

        return null
    }

    function renderContent() {
        return (
            <div className="adminContentBox">
                <div className="adminViewHeader">
                    <h3 className="adminViewTitle">{activeView}</h3>
                    <div className="adminViewActions">
                        {(activeView === 'IOL Exchange') && ['Real', 'Test', 'All'].map(type =>
                            <button key={type}
                                className={'exportBtn' + (iolxCaseType === type ? ' active' : '')}
                                onClick={() => setIolxCaseType(type)}>
                                {type}
                            </button>
                        )}
                        <button className="exportBtn" onClick={() => exportData('csv')}>
                            <FaFileCsv /> CSV
                        </button>
                        <button className="exportBtn" onClick={() => exportData('xlsx')}>
                            <FaFileExcel /> XLSX
                        </button>
                    </div>
                </div>
                
                {renderAverages()}
                
                <div className="adminTableContainer">
                    <DataTable
                        columns={getActiveColumns()}
                        data={tableData}
                        theme="medicalDark"
                        pagination
                        paginationPerPage={15}
                        paginationRowsPerPageOptions={[15, 30, 50, 100]}
                        highlightOnHover
                        pointerOnHover
                        responsive
                        progressPending={isLoading}
                        progressComponent={
                            <div className="loadingContainer">
                                <Spinner animation="border" variant="info" />
                                <span>Fetching data...</span>
                            </div>
                        }
                        noDataComponent={<div className="noDataContainer">{loadError || 'No records found for this section.'}</div>}
                    />
                </div>
            </div>
        )
    }

    return (
        <div className="adminPage">
            <style>
                @import url('https://fonts.googleapis.com/css2?family=Titillium+Web&display=swap');
            </style>

            <BarHeader />

            <div className="adminPageContent vCenter">
                <div className="styledBox adminContainer">
                    <Row className="adminRow">
                        <Col xs={12} md={3} lg={2} className="adminSidebar">
                            
                            <div className="adminMenuSection">
                                <div 
                                    className="adminMenuItem"
                                    onClick={() => setIsIolMenuOpen(!isIolMenuOpen)}
                                >
                                    <span className="adminMenuIconWrapper">
                                        <FaDatabase className="adminMenuIcon" />
                                        IolOptimization.com
                                    </span>
                                    {isIolMenuOpen ? <VscChevronDown /> : <VscChevronRight />}
                                </div>
                                
                                {isIolMenuOpen && (
                                    <div className="adminSubmenu">
                                        <div 
                                            className={`adminSubmenuItem ${activeView === 'Constant Optimizator' ? 'active' : ''}`}
                                            onClick={() => setActiveView('Constant Optimizator')}
                                        >
                                            Constant Optimizator
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="adminMenuSection">
                                <div 
                                    className="adminMenuItem"
                                    onClick={() => setIsToricMenuOpen(!isToricMenuOpen)}
                                >
                                    <span className="adminMenuIconWrapper">
                                        <GoGraph className="adminMenuIcon" />
                                        ToricAligner.com
                                    </span>
                                    {isToricMenuOpen ? <VscChevronDown /> : <VscChevronRight />}
                                </div>
                                
                                {isToricMenuOpen && (
                                    <div className="adminSubmenu">
                                        <div 
                                            className={`adminSubmenuItem ${activeView === 'Standard Toric IOL' ? 'active' : ''}`}
                                            onClick={() => setActiveView('Standard Toric IOL')}
                                        >
                                            Standard Toric IOL
                                        </div>
                                        <div 
                                            className={`adminSubmenuItem ${activeView === 'Phakic / Piggyback Aligner' ? 'active' : ''}`}
                                            onClick={() => setActiveView('Phakic / Piggyback Aligner')}
                                        >
                                            Phakic / Piggyback Aligner
                                        </div>
                                        <div 
                                            className={`adminSubmenuItem ${activeView === 'SIA Calculator' ? 'active' : ''}`}
                                            onClick={() => setActiveView('SIA Calculator')}
                                        >
                                            SIA Calculator
                                        </div>
                                        <div
                                            className={`adminSubmenuItem ${activeView === 'IOL Exchange' ? 'active' : ''}`}
                                            onClick={() => setActiveView('IOL Exchange')}
                                        >
                                            IOL Exchange
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="adminMenuSection">
                                <div 
                                    className={`adminMenuItem ${activeView === 'Users' ? 'active' : ''}`}
                                    onClick={() => setActiveView('Users')}
                                >
                                    <span className="adminMenuIconWrapper">
                                        <FaUsers className="adminMenuIcon" />
                                        Users
                                    </span>
                                </div>
                            </div>

                        </Col>

                        <Col xs={12} md={9} lg={10} className="adminContentArea">
                            {renderContent()}
                        </Col>
                    </Row>
                </div>
            </div>
        </div>
    )
}